import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, TextField, Typography } from "@mui/material";
import { AthleteGoalConfigurationForCompliancePageFragment, GoalTrackerGoalForComplianceFragment } from "apps/web/src/types";
import { useDateFormatting } from "shared-ui/contexts/Locale/useDateFormatting";
import { useMemo, useState } from "react";
import { getRowDataFromGoalForTable, GoalCompletionStatus, isValidInputGoalCompliance } from "./utils";

interface EditAthleteGoalComplianceDialogProps {
    open: boolean;
    row: GoalTrackerGoalForComplianceFragment;
    onClose: () => void;
    goalConfiguration: AthleteGoalConfigurationForCompliancePageFragment;
    onSubmit: (oalId: string, value: number, completed: boolean) => void;
}


const EditAthleteGoalComplianceDialog = ({ open, onClose, goalConfiguration, row, onSubmit }: EditAthleteGoalComplianceDialogProps) => {
    const { target } = goalConfiguration;
    const hasTarget = target !== null;
    const { formatTimeInTimezoneWithLocale, formatDateWithLocale } = useDateFormatting();

    const rowData = useMemo(() =>
        getRowDataFromGoalForTable(row, goalConfiguration, formatDateWithLocale, formatTimeInTimezoneWithLocale),
        [row, goalConfiguration, formatDateWithLocale, formatTimeInTimezoneWithLocale]
    );
    const [completionStatus, setCompletionStatus] = useState<GoalCompletionStatus>(rowData.completed === GoalCompletionStatus.Yes ? GoalCompletionStatus.Yes : GoalCompletionStatus.None);
    const [value, setValue] = useState<number>(rowData.value || 0);

    const isValidInput = isValidInputGoalCompliance(value, target, completionStatus);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
        >
            <DialogHeader onClose={onClose} />
            <DialogContent>
                <TargetInfoBox rowData={rowData} hasTarget={hasTarget} />
                {hasTarget ? (
                    <TextField
                        placeholder="Enter value"
                        value={value || ""}
                        label="Input"
                        type="number"
                        onChange={e => { setValue(Number(e.target.value)) }}
                        sx={{ width: 240 }}
                        error={value > target?.value}
                        helperText={value > target?.value ? "Value cannot be greater than target" : ""} 
                    />
                ) : (
                    <TextField
                        sx={{ width: 240 }}
                        select
                        inputProps={{ "aria-label": "Select" }}
                        value={completionStatus}
                        onChange={(e) => setCompletionStatus(e.target.value as GoalCompletionStatus)}
                        label="Select"
                    >
                        <MenuItem value={GoalCompletionStatus.None}>Select</MenuItem>
                        <MenuItem value={GoalCompletionStatus.Yes}>{GoalCompletionStatus.Yes}</MenuItem>
                        <MenuItem value={GoalCompletionStatus.No}>{GoalCompletionStatus.No}</MenuItem>
                    </TextField>
                )}
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" onClick={onClose}>
                    Cancel
                </Button>
                <Button onClick={() => onSubmit(rowData.id, value, completionStatus === "Yes")} disabled={!isValidInput}>Save</Button>
            </DialogActions>
        </Dialog >
    );
};

const DialogHeader = ({ onClose }: { onClose: () => void }) => (
    <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="h3">Edit Compliance</Typography>
            <IconButton onClick={onClose} size="large">
                <CloseIcon />
            </IconButton>
        </Box>
    </DialogTitle>
);

const TargetInfoBox = ({ rowData, hasTarget }: { rowData: any, hasTarget: boolean }) => {
    const targetFields = hasTarget && (
        <>
            {' | '}
            <Typography variant="body2Medium" color="mediumEmphasisTextLight">Target: </Typography>{rowData.target}
            {' | '}
            <Typography variant="body2Medium" color="mediumEmphasisTextLight">Unit: </Typography>{rowData.targetUnit}
        </>
    );
    return (
        <Box sx={theme => ({
            display: 'flex',
            borderRadius: 1,
            border: 1,
            p: 2,
            flexDirection: 'column',
            borderColor: theme.palette.greyscaleLight[200],
            bgcolor: theme.palette.greyscaleLight[50]
        })}>
            <Typography variant="body2" color="mediumEmphasisTextLight">
                <Typography variant="body2Medium" color="mediumEmphasisTextLight">Date: </Typography>{rowData.date}
                {targetFields}
            </Typography>
        </Box>
    )
};

export default EditAthleteGoalComplianceDialog;
